import React, {useEffect, useMemo, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    GridItem,
    HStack,
    Icon,
    IconButton,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Select,
    Stack,
    Switch,
    Text,
    Textarea,
    Wrap,
    WrapItem,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {
    MdAdd,
    MdArrowDownward,
    MdArrowUpward,
    MdDelete,
    MdFitnessCenter,
    MdMusicNote,
} from "react-icons/md";

export const PT_CATEGORIES = [
    {value: "WARMUP", label: "Warm Up", color: "orange"},
    {value: "CARDIO", label: "Cardio", color: "red"},
    {value: "STRENGTH", label: "Strength", color: "purple"},
    {value: "DRILL", label: "Drill / March", color: "blue"},
    {value: "YOGA", label: "Yoga / Stretch", color: "green"},
    {value: "COOLDOWN", label: "Cool Down", color: "teal"},
];

export const WEEK_DAYS = [
    {value: "MON", label: "Mon"},
    {value: "TUE", label: "Tue"},
    {value: "WED", label: "Wed"},
    {value: "THU", label: "Thu"},
    {value: "FRI", label: "Fri"},
    {value: "SAT", label: "Sat"},
    {value: "SUN", label: "Sun"},
];

// Seconds of active + rest time for one exercise across all its repetitions.
export const exerciseSeconds = (ex) =>
    Math.max(0, (Number(ex.duration) || 0) * (Number(ex.reps) || 1)) +
    Math.max(0, (Number(ex.rest) || 0) * ((Number(ex.reps) || 1) - 1));

export const routineSeconds = (routine) =>
    (routine.exercises || []).reduce((sum, ex) => sum + exerciseSeconds(ex), 0);

export const formatDuration = (totalSeconds) => {
    const s = Math.round(totalSeconds || 0);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m === 0) return `${rem}s`;
    return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
};

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const blankExercise = () => ({
    id: uid(),
    name: "",
    tuneId: "",
    tuneName: "",
    tuneUrl: "",
    duration: 30,
    reps: 3,
    rest: 10,
});

const blankRoutine = () => ({
    id: uid(),
    name: "",
    description: "",
    category: "WARMUP",
    schedule: {days: ["MON", "TUE", "WED", "THU", "FRI"], time: "07:15"},
    enabled: true,
    exercises: [blankExercise()],
    createdAt: Date.now(),
});

function RoutineBuilderModal({isOpen, onClose, onSave, tunes = [], initial}) {
    const toast = useToast();
    const cardBg = useColorModeValue("gray.50", "gray.800");
    const borderCol = useColorModeValue("gray.200", "gray.600");
    const [routine, setRoutine] = useState(blankRoutine());

    useEffect(() => {
        if (isOpen) {
            setRoutine(
                initial
                    ? JSON.parse(JSON.stringify(initial))
                    : blankRoutine()
            );
        }
    }, [isOpen, initial]);

    const total = useMemo(() => routineSeconds(routine), [routine]);

    const patch = (fields) => setRoutine((prev) => ({...prev, ...fields}));

    const patchExercise = (id, fields) =>
        setRoutine((prev) => ({
            ...prev,
            exercises: prev.exercises.map((ex) => (ex.id === id ? {...ex, ...fields} : ex)),
        }));

    const addExercise = () =>
        setRoutine((prev) => ({...prev, exercises: [...prev.exercises, blankExercise()]}));

    const removeExercise = (id) =>
        setRoutine((prev) => ({
            ...prev,
            exercises: prev.exercises.filter((ex) => ex.id !== id),
        }));

    const moveExercise = (index, dir) =>
        setRoutine((prev) => {
            const next = [...prev.exercises];
            const target = index + dir;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return {...prev, exercises: next};
        });

    const toggleDay = (day) =>
        setRoutine((prev) => {
            const days = prev.schedule.days.includes(day)
                ? prev.schedule.days.filter((d) => d !== day)
                : [...prev.schedule.days, day];
            return {...prev, schedule: {...prev.schedule, days}};
        });

    const handleTuneChange = (id, tuneId) => {
        const tune = tunes.find((t) => t.id === tuneId);
        patchExercise(id, {
            tuneId,
            tuneName: tune?.name || "",
            tuneUrl: tune?.url || "",
        });
    };

    const handleSave = () => {
        if (!routine.name.trim()) {
            toast({title: "Give the routine a name", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        const exercises = routine.exercises.filter((ex) => ex.name.trim());
        if (exercises.length === 0) {
            toast({title: "Add at least one exercise", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        onSave({...routine, name: routine.name.trim(), exercises});
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(3px)"/>
            <ModalContent borderRadius="20px">
                <ModalHeader>
                    <HStack spacing={3}>
                        <Flex w="38px" h="38px" borderRadius="10px" bg="teal.500" color="white" align="center" justify="center">
                            <Icon as={MdFitnessCenter}/>
                        </Flex>
                        <Box>
                            <Text fontSize="lg">{initial ? "Edit PT Routine" : "New PT Routine"}</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="normal">
                                Sequence exercises, assign tunes, reps &amp; rest — then schedule it.
                            </Text>
                        </Box>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Stack spacing={5}>
                        {/* --- meta --- */}
                        <Grid templateColumns={{base: "1fr", md: "2fr 1fr"}} gap={4}>
                            <FormControl isRequired>
                                <FormLabel>Routine name</FormLabel>
                                <Input
                                    value={routine.name}
                                    onChange={(e) => patch({name: e.target.value})}
                                    placeholder="e.g. Morning Assembly Drill"
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Category</FormLabel>
                                <Select value={routine.category} onChange={(e) => patch({category: e.target.value})}>
                                    {PT_CATEGORIES.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <FormControl>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                                value={routine.description}
                                onChange={(e) => patch({description: e.target.value})}
                                placeholder="Short note shown on the routine card"
                                rows={2}
                            />
                        </FormControl>

                        {/* --- schedule --- */}
                        <Box bg={cardBg} borderRadius="14px" p={4}>
                            <Text fontWeight="bold" mb={3}>Schedule</Text>
                            <Wrap spacing={2} mb={4}>
                                {WEEK_DAYS.map((d) => {
                                    const active = routine.schedule.days.includes(d.value);
                                    return (
                                        <WrapItem key={d.value}>
                                            <Button
                                                size="sm"
                                                borderRadius="full"
                                                variant={active ? "solid" : "outline"}
                                                colorScheme={active ? "teal" : "gray"}
                                                onClick={() => toggleDay(d.value)}
                                            >
                                                {d.label}
                                            </Button>
                                        </WrapItem>
                                    );
                                })}
                            </Wrap>
                            <Grid templateColumns={{base: "1fr", md: "1fr 1fr"}} gap={4}>
                                <FormControl>
                                    <FormLabel fontSize="sm">Start time</FormLabel>
                                    <Input
                                        type="time"
                                        value={routine.schedule.time}
                                        onChange={(e) => patch({schedule: {...routine.schedule, time: e.target.value}})}
                                    />
                                </FormControl>
                                <FormControl display="flex" alignItems="center" pt={{md: "28px"}}>
                                    <Switch
                                        isChecked={routine.enabled}
                                        onChange={(e) => patch({enabled: e.target.checked})}
                                        colorScheme="teal"
                                        mr={3}
                                    />
                                    <FormLabel mb={0} fontSize="sm">
                                        {routine.enabled ? "Scheduled (active)" : "Draft (paused)"}
                                    </FormLabel>
                                </FormControl>
                            </Grid>
                        </Box>

                        <Divider/>

                        {/* --- exercises --- */}
                        <Flex justify="space-between" align="center">
                            <Text fontWeight="bold">
                                Exercise sequence{" "}
                                <Badge ml={2} colorScheme="teal" borderRadius="full">{routine.exercises.length}</Badge>
                            </Text>
                            <Text fontSize="sm" color="gray.500">Total ≈ {formatDuration(total)}</Text>
                        </Flex>

                        <Stack spacing={3}>
                            {routine.exercises.map((ex, index) => (
                                <Box key={ex.id} border="1px solid" borderColor={borderCol} borderRadius="14px" p={4}>
                                    <Flex align="center" mb={3} gap={2}>
                                        <Flex
                                            w="26px" h="26px" flexShrink={0}
                                            borderRadius="full" bg="teal.500" color="white"
                                            align="center" justify="center" fontSize="xs" fontWeight="bold"
                                        >
                                            {index + 1}
                                        </Flex>
                                        <Input
                                            variant="flushed"
                                            fontWeight="semibold"
                                            placeholder="Exercise name (e.g. Jumping Jacks)"
                                            value={ex.name}
                                            onChange={(e) => patchExercise(ex.id, {name: e.target.value})}
                                        />
                                        <IconButton aria-label="Move up" size="sm" variant="ghost" icon={<MdArrowUpward/>}
                                                    isDisabled={index === 0} onClick={() => moveExercise(index, -1)}/>
                                        <IconButton aria-label="Move down" size="sm" variant="ghost" icon={<MdArrowDownward/>}
                                                    isDisabled={index === routine.exercises.length - 1} onClick={() => moveExercise(index, 1)}/>
                                        <IconButton aria-label="Remove" size="sm" variant="ghost" colorScheme="red" icon={<MdDelete/>}
                                                    isDisabled={routine.exercises.length === 1} onClick={() => removeExercise(ex.id)}/>
                                    </Flex>

                                    <Grid templateColumns={{base: "1fr 1fr", md: "2fr 1fr 1fr 1fr"}} gap={3}>
                                        <GridItem colSpan={{base: 2, md: 1}}>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color="gray.500">
                                                    <Icon as={MdMusicNote} mr={1}/>PT Tune
                                                </FormLabel>
                                                <Select
                                                    size="sm"
                                                    placeholder={tunes.length ? "Silent (timer only)" : "No tunes available"}
                                                    value={ex.tuneId}
                                                    onChange={(e) => handleTuneChange(ex.id, e.target.value)}
                                                >
                                                    {tunes.map((t) => (
                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </GridItem>
                                        <FormControl>
                                            <FormLabel fontSize="xs" color="gray.500">Duration (s)</FormLabel>
                                            <NumberInput size="sm" min={1} max={3600} value={ex.duration}
                                                         onChange={(_, v) => patchExercise(ex.id, {duration: Number.isNaN(v) ? 1 : v})}>
                                                <NumberInputField/>
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper/>
                                                    <NumberDecrementStepper/>
                                                </NumberInputStepper>
                                            </NumberInput>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" color="gray.500">Repetitions</FormLabel>
                                            <NumberInput size="sm" min={1} max={100} value={ex.reps}
                                                         onChange={(_, v) => patchExercise(ex.id, {reps: Number.isNaN(v) ? 1 : v})}>
                                                <NumberInputField/>
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper/>
                                                    <NumberDecrementStepper/>
                                                </NumberInputStepper>
                                            </NumberInput>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" color="gray.500">Rest (s)</FormLabel>
                                            <NumberInput size="sm" min={0} max={600} value={ex.rest}
                                                         onChange={(_, v) => patchExercise(ex.id, {rest: Number.isNaN(v) ? 0 : v})}>
                                                <NumberInputField/>
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper/>
                                                    <NumberDecrementStepper/>
                                                </NumberInputStepper>
                                            </NumberInput>
                                        </FormControl>
                                    </Grid>
                                    <Text fontSize="xs" color="gray.400" mt={2}>
                                        {ex.reps}× {ex.duration}s work
                                        {ex.rest > 0 ? ` · ${ex.rest}s rest between reps` : ""} ≈ {formatDuration(exerciseSeconds(ex))}
                                    </Text>
                                </Box>
                            ))}
                        </Stack>

                        <Button leftIcon={<MdAdd/>} variant="outline" colorScheme="teal" onClick={addExercise}>
                            Add exercise
                        </Button>
                    </Stack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
                    <Button colorScheme="teal" onClick={handleSave}>
                        {initial ? "Save changes" : "Create routine"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default RoutineBuilderModal;
